import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './Input';

/**
 * Eigenes Zeitfeld mit getrennten Stunden/Minuten-Eingaben und Dropdown-Auswahl.
 * - Klick auf die Stundenzahl öffnet eine Liste mit 0-23
 * - Klick auf die Minutenzahl öffnet eine Liste mit Vielfachen von 5 (0, 5, 10 ... 55)
 * - Manuelle Eingabe erlaubt beliebige Minutenwerte (0-59)
 * Interface identisch mit <input type="time">:
 *   value: "HH:MM" oder ""
 *   onChange: (e) => e.target.value ist "HH:MM" oder ""
 */

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

const parseValue = (val) => {
  if (!val) return { h: '', m: '' };
  const [h, m] = val.split(':');
  return { h: h || '', m: m || '' };
};

export const TimeInput = ({ value, onChange, className = '', id, disabled }) => {
  const [h, setH] = useState(() => parseValue(value).h);
  const [m, setM] = useState(() => parseValue(value).m);
  const [openDropdown, setOpenDropdown] = useState(null); // 'hours' | 'minutes' | null

  const containerRef = useRef(null);
  const minutesInputRef = useRef(null);
  // Letzter von uns emittierter Wert - verhindert Ruecksync unserer eigenen Aenderungen
  const ownEmit = useRef(value || '');

  // Nur echte externe Wertaenderungen (z. B. Formular-Reset) uebernehmen
  useEffect(() => {
    if (value === ownEmit.current) return;
    ownEmit.current = value || '';
    const parsed = parseValue(value);
    setH(parsed.h);
    setM(parsed.m);
  }, [value]);

  // Dropdown bei Klick ausserhalb schliessen
  useEffect(() => {
    if (!openDropdown) return;
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [openDropdown]);

  const notify = useCallback((newH, newM) => {
    if (!onChange) return;
    const hStr = newH ? newH.toString().padStart(2, '0') : '';
    const mStr = newM ? newM.toString().padStart(2, '0') : '';
    const timeStr = hStr && mStr ? `${hStr}:${mStr}` : '';
    ownEmit.current = timeStr;
    onChange({ target: { value: timeStr } });
  }, [onChange]);

  // Waehrend des Tippens: nur lokalen State aktualisieren, NICHT notifizieren.
  // Dadurch kann die zweite Ziffer ungehindert eingegeben werden.
  const handleHoursChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    setH(val);
    // Auto-Advance: 2 Ziffern eingegeben, oder erste Ziffer 3-9 (keine gueltige 2-stellige Stunde moeglich)
    if (val.length === 2 || (val.length === 1 && parseInt(val, 10) >= 3)) {
      minutesInputRef.current?.focus();
    }
  };

  const handleMinutesChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    setM(val);
  };

  // Beim Verlassen des Feldes: auf gueltigen Bereich klemmen und notifizieren.
  // e.relatedTarget-Pruefung: Fokus bleibt im Component -> nicht schliessen/notifizieren.
  const handleHoursBlur = (e) => {
    if (containerRef.current?.contains(e.relatedTarget)) return;
    if (h !== '') {
      const committed = Math.min(parseInt(h, 10), 23).toString().padStart(2, '0');
      setH(committed);
      notify(committed, m);
    } else {
      notify('', m);
    }
    setOpenDropdown(null);
  };

  const handleMinutesBlur = (e) => {
    if (containerRef.current?.contains(e.relatedTarget)) return;
    if (m !== '') {
      const committed = Math.min(parseInt(m, 10), 59).toString().padStart(2, '0');
      setM(committed);
      notify(h, committed);
    } else {
      notify(h, '');
    }
    setOpenDropdown(null);
  };

  const selectHour = (option) => {
    setH(option);
    notify(option, m);
    setOpenDropdown(null);
    minutesInputRef.current?.focus();
  };

  const selectMinute = (option) => {
    setM(option);
    notify(h, option);
    setOpenDropdown(null);
  };

  return (
    <div
      ref={containerRef}
      id={id}
      className={`time-input ${className}`}
      aria-disabled={disabled || undefined}
    >
      <div className="time-input__part">
        <Input
          type="text"
          inputMode="numeric"
          value={h}
          onChange={handleHoursChange}
          onBlur={handleHoursBlur}
          onClick={() => !disabled && setOpenDropdown('hours')}
          onKeyDown={(e) => e.key === ':' && minutesInputRef.current?.focus()}
          disabled={disabled}
          maxLength={2}
          placeholder="HH"
          className="time-input__field"
          aria-label="Stunden"
        />
        {openDropdown === 'hours' && (
          <div className="time-dropdown" role="listbox" aria-label="Stunde waehlen">
            {HOUR_OPTIONS.map(opt => (
              <div
                key={opt}
                role="option"
                aria-selected={h === opt}
                className={`time-dropdown__item${h === opt ? ' time-dropdown__item--active' : ''}`}
                onMouseDown={() => selectHour(opt)}
              >
                {opt}
              </div>
            ))}
          </div>
        )}
      </div>

      <span className="time-input__sep" aria-hidden="true">:</span>

      <div className="time-input__part">
        <Input
          ref={minutesInputRef}
          type="text"
          inputMode="numeric"
          value={m}
          onChange={handleMinutesChange}
          onBlur={handleMinutesBlur}
          onClick={() => !disabled && setOpenDropdown('minutes')}
          disabled={disabled}
          maxLength={2}
          placeholder="MM"
          className="time-input__field"
          aria-label="Minuten"
        />
        {openDropdown === 'minutes' && (
          <div className="time-dropdown" role="listbox" aria-label="Minute waehlen">
            {MINUTE_OPTIONS.map(opt => (
              <div
                key={opt}
                role="option"
                aria-selected={m === opt}
                className={`time-dropdown__item${m === opt ? ' time-dropdown__item--active' : ''}`}
                onMouseDown={() => selectMinute(opt)}
              >
                {opt}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
